import React, { useEffect, useRef, useState } from "react";

import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import {
  Badge,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  Grow,
  List,
  ListItem,
  Paper,
  Popper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { TopBarIconButton } from "./top-bar-icon-button";

const StyledPaper = styled(Paper)({
  transformOrigin: "top right",
  backgroundImage: "none",
});

const StyledList = styled(List)(({ theme }) => ({
  width: theme.spacing(40),
  maxHeight: 540,
  overflow: "auto",
  padding: theme.spacing(1, 0),
  scrollbarColor: `${theme.palette.grey[500]} ${theme.palette.background.paper}`,
}));

const StyledListItem = styled(ListItem)({
  display: "flex",
  flexDirection: "column",
});

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(1, 0),
}));

const Loading = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  margin: theme.spacing(3, 0),
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)({
  display: "flex",
  width: "100%",
});

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  flex: 1,
  border: "none",
  borderBottom: "solid 1px",
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  borderColor: theme.palette.grey[500],
}));

interface Notification {
  id: number;
  title: string;
  text: string;
  post_date: string;
  link?: string;
  team: string;
}

const Notifications = () => {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [displayNotifications, setDisplayNotifications] = useState<
    Notification[]
  >([]);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [unseenNotifications, setUnseenNotifications] = useState<
    Notification[]
  >([]);
  const [unseenNotificationsIds, setUnseenNotificationsIds] = useState<
    number[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [toggleValue, setToggleValue] = React.useState("unseen");

  const anchorRef = useRef(null);

  const handleToggleChange = (
    event: React.MouseEvent<HTMLElement>,
    newToggleValue: string,
  ) => {
    setToggleValue(newToggleValue);
    switch (newToggleValue) {
      case "unseen":
        setDisplayNotifications(unseenNotifications);
        break;
      case "latest":
        setDisplayNotifications(allNotifications.slice(0, 3));
        break;
      case "all":
        setDisplayNotifications(allNotifications);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    async function fetchNotifications() {
      try {
        const response = await fetch("/notifications.json");
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data: Notification[] = await response.json();
        data.sort((a, b) => {
          return new Date(b.post_date) > new Date(a.post_date) ? 1 : -1;
        });
        const seenIds: number[] = JSON.parse(
          localStorage.getItem("seenNotifications") || "[]",
        );

        setAllNotifications(data);

        const unseen = data.filter(
          (notification) => !seenIds.includes(notification.id),
        );

        if (unseen.length > 0) {
          setDisplayNotifications(unseen);
          setUnseenNotificationsIds(
            unseen.map((notification) => notification.id),
          );
        } else {
          const recentNotifications = data.slice(0, 3);
          setDisplayNotifications(recentNotifications);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchNotifications();
  }, []);

  const handleToggleNotifications = () => {
    setNotificationsOpen((prevOpen) => !prevOpen);

    if (!notificationsOpen) {
      const storedSeenIds = JSON.parse(
        localStorage.getItem("seenNotifications") || "[]",
      );
      const currentSeenIds = displayNotifications.map((n) => n.id);

      // Combine the two arrays and remove duplicates
      const updatedSeenIds = Array.from(
        new Set([...storedSeenIds, ...currentSeenIds]),
      );

      localStorage.setItem("seenNotifications", JSON.stringify(updatedSeenIds));
      setUnseenNotifications([]);
    }
  };

  const handleCloseNotifications = () => {
    setNotificationsOpen(false);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <TopBarIconButton
          ref={anchorRef}
          onClick={handleToggleNotifications}
          data-testid={"bell-notification-icon"}
        >
          <Badge
            badgeContent={unseenNotificationsIds.length}
            color="error"
            data-testid={"badge"}
          >
            <NotificationsNoneRoundedIcon fontSize="small" />
          </Badge>
        </TopBarIconButton>
      </Tooltip>
      <Popper
        open={notificationsOpen}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        disablePortal
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleCloseNotifications}>
            <Grow in={notificationsOpen} {...TransitionProps}>
              <StyledPaper>
                <StyledToggleButtonGroup
                  color="primary"
                  value={toggleValue}
                  exclusive
                  onChange={handleToggleChange}
                  aria-label="Platform"
                >
                  <StyledToggleButton value="unseen">Unseen</StyledToggleButton>
                  <StyledToggleButton value="latest">Latest</StyledToggleButton>
                  <StyledToggleButton value="all">All</StyledToggleButton>
                </StyledToggleButtonGroup>
                <StyledList>
                  {loading ? (
                    <Loading>
                      <CircularProgress
                        size={32}
                        data-testid={"circular-progress"}
                      />
                    </Loading>
                  ) : displayNotifications.length ? (
                    displayNotifications.map((notification, index) => (
                      <React.Fragment key={notification.id}>
                        <StyledListItem alignItems="flex-start">
                          <Typography variant="h6">
                            <b>{notification.title}</b>
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {notification.text}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            <b>Team: {notification.team}</b>
                          </Typography>
                          {notification.link && (
                            <>
                              <Button
                                style={{
                                  padding: "0",
                                  textTransform: "none",
                                }}
                                href={notification.link}
                                target="_blank"
                                color="primary"
                              >
                                Learn more
                              </Button>
                            </>
                          )}
                          <Typography variant="caption" color="textSecondary">
                            <b>{notification.post_date || ""}</b>
                          </Typography>
                        </StyledListItem>
                        {index < displayNotifications.length - 1 && (
                          <StyledDivider />
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      align="center"
                    >
                      No new notifications
                    </Typography>
                  )}
                </StyledList>
              </StyledPaper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default Notifications;
